import React from 'react';
import { Property } from '../../types/property.types';
import { Send, PartyPopper, Heart, AlertCircle } from 'lucide-react';

export type ChatBubbleType = 'text' | 'property' | 'system';

interface ChatBubbleProps {
  message: string;
  timestamp: string;
  isOwn?: boolean;
  type?: ChatBubbleType;
  property?: Property;
  propertyMatchScore?: number;
  onPropertyClick?: () => void;
  className?: string;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  timestamp,
  isOwn = false,
  type = 'text',
  property,
  propertyMatchScore,
  onPropertyClick,
  className = '',
}) => {
  if (type === 'property' && property) {
    return (
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} mb-4 ${className}`}>
        <button
          type="button"
          onClick={onPropertyClick}
          className="text-left bg-gradient-to-br from-green-50 to-primary-50 rounded-2xl p-3 max-w-[280px] shadow-md border border-green-100 cursor-pointer hover:shadow-lg transition-all"
        >
          <div className="h-40 rounded-xl overflow-hidden mb-3 relative">
            <img
              src={property.photoUrls?.[property.primaryPhotoIndex || 0] || '/placeholder.jpg'}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            {propertyMatchScore && (
              <div className="absolute top-2 right-2 bg-white/95 backdrop-blur rounded-full px-2 py-1 text-xs font-bold text-primary-300">
                {propertyMatchScore}%
              </div>
            )}
          </div>
          <h4 className="font-bold text-sm mb-1 line-clamp-1">{property.title}</h4>
          <p className="text-primary-300 font-bold text-sm mb-2">
            R{property.monthlyRent.toLocaleString()}/mo
          </p>
          <span className="block w-full text-center bg-white text-primary-300 py-2 rounded-lg text-sm font-medium border border-primary-100">
            View Details
          </span>
        </button>
        <span className="text-xs text-gray-400 mt-1 px-2">{timestamp}</span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} mb-4 ${className}`}>
      <div
        className={`rounded-2xl px-4 py-3 max-w-[75%] shadow-sm ${
          isOwn
            ? 'bg-gradient-to-r from-primary-200 to-primary-300 text-white rounded-br-md'
            : 'bg-white text-text-primary rounded-bl-md border border-gray-100'
        }`}
      >
        <p className="text-sm leading-relaxed">{message}</p>
      </div>
      <span className="text-xs text-gray-400 mt-1 px-2">{timestamp}</span>
    </div>
  );
};

interface SystemMessageProps {
  type: 'mutual_like' | 'property_like' | 'property_liked_notification' | 'inquiry_request' | 'inquiry_sent' | 'info';
  message: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  secondaryMessage?: string;
  className?: string;
}

export const SystemMessage: React.FC<SystemMessageProps> = ({
  type,
  message,
  icon,
  action,
  secondaryMessage,
  className = '',
}) => {
  const getStyles = () => {
    switch (type) {
      case 'mutual_like':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'property_like':
      case 'property_liked_notification':
        return 'bg-purple-50 border-purple-200 text-purple-900';
      case 'inquiry_request':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900';
      case 'inquiry_sent':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const getIcon = () => {
    if (icon) return icon;
    switch (type) {
      case 'mutual_like':
        return <PartyPopper className="w-4 h-4" />;
      case 'property_like':
      case 'property_liked_notification':
        return <Heart className="w-4 h-4" />;
      case 'inquiry_request':
        return <AlertCircle className="w-4 h-4" />;
      case 'inquiry_sent':
        return <Send className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getActionButtonStyle = () => {
    switch (type) {
      case 'mutual_like':
        return 'bg-green-600 hover:bg-green-700';
      case 'inquiry_request':
        return 'bg-yellow-600 hover:bg-yellow-700';
      case 'inquiry_sent':
        return 'bg-blue-600 hover:bg-blue-700';
      default:
        return 'bg-primary-300 hover:bg-primary-400';
    }
  };

  return (
    <div className={`flex justify-center mb-4 ${className}`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] w-full border rounded-xl px-4 py-3 shadow-sm ${getStyles()}`}
      >
        <div className="flex items-start gap-2">
          {getIcon() && <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>}
          <div className="flex-1">
            <p className="font-semibold text-sm">{message}</p>
            {secondaryMessage && (
              <p className="text-xs mt-1 opacity-80">{secondaryMessage}</p>
            )}
            {action && (
              <button
                onClick={action.onClick}
                disabled={action.loading}
                className={`mt-3 inline-flex items-center gap-2 px-3 py-2 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 ${getActionButtonStyle()}`}
              >
                {action.loading ? 'Loading...' : action.label}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  leftButton?: {
    icon: React.ReactNode;
    onClick: () => void;
    active?: boolean;
  };
  className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder = 'Type a message...',
  disabled = false,
  leftButton,
  className = '',
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled) {
      onSend();
    }
  };

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className="flex gap-2 items-end">
        {leftButton && (
          <button
            type="button"
            onClick={leftButton.onClick}
            className={`flex-shrink-0 p-3 rounded-xl border-2 transition-all shadow-lg ${
              leftButton.active
                ? 'bg-primary-300 border-primary-300 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-primary-200'
            }`}
          >
            {leftButton.icon}
          </button>
        )}
        <input
          className="input flex-1 text-sm md:text-base"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <button
          type="submit"
          disabled={!value.trim() || disabled}
          className="btn-primary p-2.5 md:p-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          <Send className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>
    </form>
  );
};
